<template>
    <div class="mt-6 w-full flex flex-column align-items-center justify-content-center">

        <div class="w-full flex-column align-items-center justify-content-center" :class="{ 'flex': !httpAuthorizationExchangeLoading, 'hidden': httpAuthorizationExchangeLoading}">
            <!-- NO RUNNING SESSION : Say that the user has to login/register -->
            <div class="w-full flex-column align-items-center justify-content-center gap-3" :class="{ 'flex': !loginSessionStarted, 'hidden': loginSessionStarted}">
                <span style="color: var(--theme-font-color); font-size: var(--login-maintitle-font-size); font-weight: var(--login-maintitle-font-weight);" class="mx-5 px-8 text-center">Sign in or create an account to {{ isMigration ? 'migrate old activations' : 'manage serials' }}</span>
            
                <div class="flex flex-column w-full gap-2 align-items-center mt-3">
                    <ButtonModule type="primary" severity="normal" :label="!httpAuthorizationLoading ? 'Sign In with Browser' : 'Wait...'" :disabled="httpAuthorizationLoading" @click="clickedLogin"/>
                    <span style="color: var(--theme-font-color); font-size: var(--login-subtext-font-size); font-weight: var(--login-subtext-font-weight);">
                        Don't have an account?
                        <span id="clickable-text" class="cursor-pointer" style="text-decoration: none; font-size: var(--login-subtext-font-size); font-weight: var(--login-subtext-font-weight);" @click="clickedRegister">Register.</span>
                    </span>
                </div>
            </div>

            <!-- SESSION IS RUNNING : Say that the user has to login in the browser -->
            <div v-if="loginSessionStarted" class="w-full flex flex-column align-items-center justify-content-center gap-6">
                <span style="color: var(--theme-font-color); font-size: var(--login-maintitle-font-size); font-weight: var(--login-maintitle-font-weight);" class="mx-5 px-8 text-center">Go to the browser to complete the login</span>

                <div class="flex flex-column w-full gap-2 align-items-center">
                    <span style="color: var(--theme-font-color); font-size: var(--login-subtext-font-size); font-weight: var(--login-subtext-font-weight);">
                        Not seeing the browser tab?
                        <span id="clickable-text" class="cursor-pointer" style="text-decoration: none; font-size: var(--login-subtext-font-size); font-weight: var(--login-subtext-font-weight);" @click="loginSessionStarted = false">Go back and try again.</span>
                    </span>
                </div>
            </div>
        </div>

        <div v-if="httpAuthorizationExchangeLoading" class="w-full flex flex-column align-items-center justify-content-center">
            <span style="color: var(--theme-font-color); font-size: var(--login-maintitle-font-size); font-weight: var(--login-maintitle-font-weight);" class="mx-5 px-8 text-center">Logging in</span>
            <LoadingSpinner/>
        </div>

    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, computed, onMounted, watchEffect, onBeforeUnmount } from 'vue';
// vue
import ButtonModule from '@/components/Modules/ButtonModule.vue'
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
// Others
import { useRouter } from 'vue-router'
import { useStore } from 'vuex';
import UserUtils from '@/composables/UserUtils'
import toastService from '@/composables/toastService';

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchAuthorization, fetchAuthorizationExchange } = useApiRequest()
const httpAuthorizationLoading = ref(false)
const httpAuthorizationData = ref(null)
const httpAuthorizationExchangeLoading = ref(false)
const httpAuthorizationExchangeData = ref(null)

const router = useRouter()
const store = useStore()
store.commit('applicationWebView/setIsWebView')
const { loginUser } = UserUtils(store, router)

// --- Application and machine info ---
const isMigration = computed(() => store.state.applicationWebView.isMigration)

// --- REDIRECT IF ALREADY CONNECTED ---
const isLoggedIn = computed(() => store.getters['user/isLoggedIn'])
watchEffect(() => {
    if (isLoggedIn.value && !isMigration.value) { router.push({ path: '/application/licenses' }) }
    else if (isLoggedIn.value && isMigration.value) { router.push({ path: '/application/migration' }) }
    else {
        // Stay here and set WebView size
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsResizeWindow) {
            window.webkit.messageHandlers.jsResizeWindow.postMessage({ width: 600, height: 345 });
        }
    }
})

// --- LOGIN ---
const loginSessionStarted = ref(false)
const clickedLogin = async () => {
    // HTTP request
    httpAuthorizationLoading.value = true
    try {
        httpAuthorizationData.value = await fetchAuthorization({ type: "login", redirect_uri: store.state.applicationWebView.applicationAuthorizationCallback, application_name: store.state.applicationWebView.applicationName })

        // Open the login link in browser
        let authorizationLoginCompleteLink = `${httpAuthorizationData.value.authorization_redirecting_link}?session_uid=${httpAuthorizationData.value.authorization_session_uid}`
        // Ask the app to open it
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
            window.webkit.messageHandlers.jsOpenWebPage.postMessage(authorizationLoginCompleteLink);
        }

        // Authorization session started
        loginSessionStarted.value = true
    } catch (err) {
        handleAuthorizationError(err)
    } finally {
        httpAuthorizationLoading.value = false
    }
}

// --- REGISTER ---
const clickedRegister = async () => {
    // HTTP request
    httpAuthorizationLoading.value = true
    try {
        httpAuthorizationData.value = await fetchAuthorization({ type: "register", redirect_uri: store.state.applicationWebView.applicationAuthorizationCallback, application_name: store.state.applicationWebView.applicationName })

        // Open the login link in browser
        let authorizationRegisterCompleteLink = `${httpAuthorizationData.value.authorization_redirecting_link}?session_uid=${httpAuthorizationData.value.authorization_session_uid}`
        // Ask the app to open it
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
            window.webkit.messageHandlers.jsOpenWebPage.postMessage(authorizationRegisterCompleteLink);
        }
    } catch (err) {
        handleAuthorizationError(err)
    } finally {
        httpAuthorizationLoading.value = false
    }
}

// --- AUTHORIZATION ERROR HANDLING ---
const handleAuthorizationError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Login: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // MISSING_OBJ / MISSING_OBJ_ELEM / INVALID_AUTHORIZATION
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    toastService.add({ severity: 'warn', summary: "Authorization: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else if (errorCode == 'INVALID_AUTHORIZATION') {
                    toastService.add({ severity: 'warn', summary: "Authorization: Error.", detail: errorMessage, life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Authorization: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401:
                if (errorCode == 'CLOUDFLARE_TURNSTILE_INVALID') {
                    toastService.add({ severity: 'error', summary: `Authorization: Cloudflare Verification Error.`, detail: "The Cloudflare verification is no longer valid — this can happen if too much time passes before submitting. Please redo the verification and try again.", life: 10000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Authorization: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR') {
                    toastService.add({ severity: 'error', summary: "Authorization: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Authorization: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Authorization: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Authorization: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}


// --- Authorization Code Flow ---

// After the login in the browser, the server responds with a callback to the application with the authorization code and the state. The application notifies the webview and let it make the request to exchange this authorization token with the actual access/refresh tokens
onMounted(() => {
    window.appSentAuthorizationCallback = async (authorizationInfo) => { // authorizationInfo contains the authorization_code and the state for the tokens exchange
        httpAuthorizationExchangeLoading.value = true
        try {
            httpAuthorizationExchangeData.value = await fetchAuthorizationExchange({ authorization_code: authorizationInfo.code, redirect_uri: store.state.applicationWebView.applicationAuthorizationCallback }, { state: authorizationInfo.state })

            if (httpAuthorizationExchangeData.value.account_info) { // it might be possible that it is empty, in the case the worker invalidated the request because the state is not correct
                // Login the user
                loginUser(httpAuthorizationExchangeData.value)
                // Redirect to /licenses
                router.push({ path: !isMigration.value ? '/application/licenses' : '/application/migration' })
            }
        } catch (err) {
            handleAuthorizationExchangeError(err)
        } finally {
            httpAuthorizationExchangeLoading.value = false
        }
    }
})
onBeforeUnmount(() => {
    window.appSentAuthorizationCallback = () => {
        // do nothing
    }
})
// Handle an error from the server
const handleAuthorizationExchangeError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Login: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // Missing info = MISSING_OBJ / MISSING_OBJ_ELEM (400) => toast
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    toastService.add({ severity: 'warn', summary: "Login: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else if (errorCode == 'INVALID_AUTHORIZATION') {
                    toastService.add({ severity: 'warn', summary: "Login: Error.", detail: errorMessage, life: 5000 });
                    loginSessionStarted.value = false
                }
                else { toastService.add({ severity: 'error', summary: `Login: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // INVALID_AUTHORIZATION_SESSION
                if (errorCode == 'INVALID_AUTHORIZATION_SESSION') {
                    toastService.add({ severity: 'error', summary: `Login: Invalid Authorization Session.`, detail: errorMessage, life: 5000 });
                    loginSessionStarted.value = false
                }
                else { toastService.add({ severity: 'error', summary: `Login: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: `Login: Invalid Authorization Session.`, detail: errorMessage, life: 5000 });
                    loginSessionStarted.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Login: CAPTCHA Error.`, detail: "The Cloudflare verification is no longer valid — this can happen if too much time passes before submitting. Please redo the verification and try again.", life: 10000 }); } // It only happens when the CAPTCHA is not correct
                break;
            case 500: // DATABASE_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR') {
                    toastService.add({ severity: 'error', summary: "Login: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Login: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Login: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Login: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
</script>

<style scoped>
/* Go to register */
#clickable-text {
    text-decoration: none;
    color: var(--button-normal-color);
}
#clickable-text:visited {
    color: var(--button-normal-color);
}
#clickable-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>